import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  MinusIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceError } from "../../utils/ResourceError";
import { useSettings } from "../../../contexts/settings-context";
import { getOfferRevisionAsync } from "../../../slices/offer";
import { isOfferNotAuthorized, parseMoneyAmount } from "./utils";
import OfferLockedCalculationTree from "./offer-locked-calculation-tree";
import "./offer-workspace.css";

/**
 * Read-only revision comparison for the Historikk/Revisjoner tab.
 * Picks two revisions (from/to) and diffs economic totals + lines using the
 * live revision-detail contract (GET /offers/:id/revisions/:id).
 */

const NUMERIC_EPSILON = 0.005;

/** Stable-ish identity for matching a line across revisions. */
function resolveLineKey(line) {
  if (line?.source_offer_item_id != null) return `item:${line.source_offer_item_id}`;
  if (line?.source_calculation_id != null) return `calc:${line.source_calculation_id}`;
  const title = (line?.title || "").trim().toLowerCase();
  if (title) return `title:${title}:${line?.row_order ?? ""}`;
  if (line?.row_order != null) return `row:${line.row_order}`;
  return `id:${line?.id ?? ""}`;
}

function moneyChanged(a, b) {
  const na = parseMoneyAmount(a) ?? 0;
  const nb = parseMoneyAmount(b) ?? 0;
  return Math.abs(na - nb) > NUMERIC_EPSILON;
}

function textChanged(a, b) {
  return (a || "").trim() !== (b || "").trim();
}

function buildLineDiff(fromLines, toLines) {
  const fromMap = new Map();
  (fromLines || []).forEach((line) => {
    const key = resolveLineKey(line);
    if (!fromMap.has(key)) fromMap.set(key, line);
  });
  const toMap = new Map();
  (toLines || []).forEach((line) => {
    const key = resolveLineKey(line);
    if (!toMap.has(key)) toMap.set(key, line);
  });

  const added = [];
  const removed = [];
  const changed = [];
  let unchanged = 0;

  toMap.forEach((toLine, key) => {
    const fromLine = fromMap.get(key);
    if (!fromLine) {
      added.push(toLine);
      return;
    }
    const fields = [];
    if (moneyChanged(fromLine.quantity, toLine.quantity)) {
      fields.push({ field: "quantity", from: fromLine.quantity, to: toLine.quantity, kind: "number" });
    }
    if (moneyChanged(fromLine.sales_price, toLine.sales_price)) {
      fields.push({ field: "sales_price", from: fromLine.sales_price, to: toLine.sales_price, kind: "money" });
    }
    if (moneyChanged(fromLine.line_total, toLine.line_total)) {
      fields.push({ field: "line_total", from: fromLine.line_total, to: toLine.line_total, kind: "money" });
    }
    if (textChanged(fromLine.title, toLine.title)) {
      fields.push({ field: "title", from: fromLine.title, to: toLine.title, kind: "text" });
    }
    if (textChanged(fromLine.description, toLine.description)) {
      fields.push({ field: "description", from: fromLine.description, to: toLine.description, kind: "text" });
    }
    if (fields.length) {
      changed.push({ key, from: fromLine, to: toLine, fields });
    } else {
      unchanged += 1;
    }
  });

  fromMap.forEach((fromLine, key) => {
    if (!toMap.has(key)) removed.push(fromLine);
  });

  return { added, removed, changed, unchanged };
}

function DeltaBadge({ delta, unit, formatValue, invert = false }) {
  const { t } = useTranslation();
  if (delta == null || Math.abs(delta) <= NUMERIC_EPSILON) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
        <MinusIcon className="h-3.5 w-3.5" aria-hidden="true" />
        {t("offers.diff.no_change")}
      </span>
    );
  }
  const positive = delta > 0;
  // "Good" direction can be inverted (e.g. cost going up is bad).
  const good = invert ? !positive : positive;
  const tone = good ? "text-emerald-700" : "text-red-700";
  const Icon = positive ? ArrowUpIcon : ArrowDownIcon;
  const sign = positive ? "+" : "−";
  const magnitude = formatValue(Math.abs(delta));
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${tone}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      <span>
        {sign}
        {magnitude}
        {unit ? ` ${unit}` : ""}
      </span>
    </span>
  );
}

function MetricRow({ label, fromValue, toValue, delta, formatValue, unit, invert }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-t border-slate-100 px-4 py-3 sm:grid-cols-4 sm:items-center sm:gap-2">
      <div className="text-sm font-medium text-slate-700">{label}</div>
      <div className="text-sm text-slate-500 tabular-nums">
        {fromValue != null ? formatValue(fromValue) : "–"}
        {fromValue != null && unit ? ` ${unit}` : ""}
      </div>
      <div className="text-sm font-semibold text-slate-900 tabular-nums">
        {toValue != null ? formatValue(toValue) : "–"}
        {toValue != null && unit ? ` ${unit}` : ""}
      </div>
      <div className="sm:text-right">
        <DeltaBadge delta={delta} unit={unit} formatValue={formatValue} invert={invert} />
      </div>
    </div>
  );
}

export default function OfferRevisionDiff({ offer, revisions = [] }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { priceFormatter } = useSettings();

  const sortedRevisions = useMemo(
    () =>
      [...revisions].sort(
        (a, b) => (b.revision_number || 0) - (a.revision_number || 0)
      ),
    [revisions]
  );

  const [toId, setToId] = useState(null);
  const [fromId, setFromId] = useState(null);
  const [fromDetail, setFromDetail] = useState(null);
  const [toDetail, setToDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const detailCache = useRef(new Map());

  // Default selection: newest as "to", the one before it as "from".
  useEffect(() => {
    if (sortedRevisions.length < 2) return;
    const latest = sortedRevisions[0]?.id ?? null;
    const previous = sortedRevisions[1]?.id ?? null;
    setToId((prev) =>
      prev && sortedRevisions.some((r) => r.id === prev) ? prev : latest
    );
    setFromId((prev) =>
      prev && sortedRevisions.some((r) => r.id === prev) ? prev : previous
    );
  }, [sortedRevisions]);

  const fetchRevisionDetail = useCallback(
    async (revisionId) => {
      if (!offer?.id || !revisionId) return null;
      const cache = detailCache.current;
      if (cache.has(revisionId)) return cache.get(revisionId);
      const result = await dispatch(
        getOfferRevisionAsync({ offerId: offer.id, revisionId })
      );
      if (getOfferRevisionAsync.rejected.match(result)) {
        const payload = result.payload;
        const err = isOfferNotAuthorized(payload)
          ? "not_authorized"
          : "load_failed";
        const failure = new Error(err);
        failure.code = err;
        throw failure;
      }
      cache.set(revisionId, result.payload);
      return result.payload;
    },
    [dispatch, offer?.id]
  );

  const loadBoth = useCallback(async () => {
    if (!fromId || !toId) return;
    setLoading(true);
    setError(null);
    try {
      const [fromRes, toRes] = await Promise.all([
        fetchRevisionDetail(fromId),
        fetchRevisionDetail(toId),
      ]);
      setFromDetail(fromRes);
      setToDetail(toRes);
    } catch (err) {
      setError(err?.code || "load_failed");
      setFromDetail(null);
      setToDetail(null);
    } finally {
      setLoading(false);
    }
  }, [fromId, toId, fetchRevisionDetail]);

  useEffect(() => {
    loadBoth();
  }, [loadBoth]);

  const formatMoney = useCallback(
    (value) => {
      const n = parseMoneyAmount(value);
      if (n == null) return "–";
      return priceFormatter?.format ? priceFormatter.format(n) : String(n);
    },
    [priceFormatter]
  );

  const formatPercent = useCallback((value) => {
    const n = parseMoneyAmount(value);
    if (n == null) return "–";
    return `${n.toFixed(1)}`;
  }, []);

  const lineDiff = useMemo(() => {
    if (!fromDetail || !toDetail) return null;
    return buildLineDiff(fromDetail.lines, toDetail.lines);
  }, [fromDetail, toDetail]);

  if (sortedRevisions.length < 2) return null;

  const currency = toDetail?.currency || fromDetail?.currency || offer?.currency || "";

  const renderSelector = (label, value, onChange, disabledId) => (
    <label className="flex flex-1 flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value))}
        className="offer-select"
      >
        {sortedRevisions.map((rev) => (
          <option key={rev.id} value={rev.id} disabled={rev.id === disabledId}>
            {t("offers.history.revision_number", { number: rev.revision_number })}
            {rev.status ? ` · ${t(`offers.history.revision_status.${rev.status}`)}` : ""}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("offers.diff.title")}
        </h3>
        <p className="text-sm text-gray-500 mt-1">{t("offers.diff.subtitle")}</p>
      </div>

      <div className="px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        {renderSelector(t("offers.diff.from"), fromId, setFromId, toId)}
        {renderSelector(t("offers.diff.to"), toId, setToId, fromId)}
      </div>

      {loading ? (
        <div className="px-6 pb-6">
          <ResourceLoading />
        </div>
      ) : error ? (
        <div className="px-6 pb-6">
          <ResourceError
            errors={[
              error === "not_authorized"
                ? t("offers.errors.not_authorized")
                : t("offers.diff.error"),
            ]}
            onRetry={loadBoth}
          />
        </div>
      ) : !fromDetail || !toDetail ? (
        <div className="px-6 pb-6 text-center text-sm text-slate-500">
          {t("offers.diff.empty")}
        </div>
      ) : fromId === toId ? (
        <div className="px-6 pb-6 text-center text-sm text-slate-500">
          {t("offers.diff.same_revision")}
        </div>
      ) : (
        <div className="px-6 pb-6 space-y-6">
          {/* Economic summary */}
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <div className="hidden grid-cols-4 gap-2 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:grid">
              <span>{t("offers.diff.metric")}</span>
              <span>
                {t("offers.history.revision_number", {
                  number: fromDetail.revision_number,
                })}
              </span>
              <span>
                {t("offers.history.revision_number", {
                  number: toDetail.revision_number,
                })}
              </span>
              <span className="text-right">{t("offers.diff.change")}</span>
            </div>
            <MetricRow
              label={t("offers.diff.offer_total")}
              fromValue={parseMoneyAmount(fromDetail.total_ex_tax)}
              toValue={parseMoneyAmount(toDetail.total_ex_tax)}
              delta={
                (parseMoneyAmount(toDetail.total_ex_tax) ?? 0) -
                (parseMoneyAmount(fromDetail.total_ex_tax) ?? 0)
              }
              formatValue={formatMoney}
            />
            <MetricRow
              label={t("offers.diff.contribution_margin")}
              fromValue={parseMoneyAmount(fromDetail.contribution_margin)}
              toValue={parseMoneyAmount(toDetail.contribution_margin)}
              delta={
                (parseMoneyAmount(toDetail.contribution_margin) ?? 0) -
                (parseMoneyAmount(fromDetail.contribution_margin) ?? 0)
              }
              formatValue={formatMoney}
            />
            <MetricRow
              label={t("offers.diff.contribution_margin_percent")}
              fromValue={parseMoneyAmount(fromDetail.contribution_margin_percent)}
              toValue={parseMoneyAmount(toDetail.contribution_margin_percent)}
              delta={
                (parseMoneyAmount(toDetail.contribution_margin_percent) ?? 0) -
                (parseMoneyAmount(fromDetail.contribution_margin_percent) ?? 0)
              }
              formatValue={formatPercent}
              unit="%"
            />
          </div>

          {/* Line diff */}
          {lineDiff && (
            <LineDiffSection
              lineDiff={lineDiff}
              formatMoney={formatMoney}
              currency={currency}
            />
          )}

          {/* REV-001: locked kalkyle trees from snapshot */}
          <div className="grid gap-4 lg:grid-cols-2">
            <OfferLockedCalculationTree
              calculations={fromDetail?.snapshot_data?.calculations}
              caption={t("offers.diff.locked_calc_from", {
                number: fromDetail.revision_number,
              })}
            />
            <OfferLockedCalculationTree
              calculations={toDetail?.snapshot_data?.calculations}
              caption={t("offers.diff.locked_calc_to", {
                number: toDetail.revision_number,
              })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function LineChip({ tone, icon: Icon, count, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {count} {label}
    </span>
  );
}

function LineTitle({ line, t }) {
  const title = (line?.title || "").trim();
  return (
    <span className="font-medium text-slate-800">
      {title || t("offers.diff.untitled_line")}
    </span>
  );
}

function LineDiffSection({ lineDiff, formatMoney, currency }) {
  const { t } = useTranslation();
  const { added, removed, changed, unchanged } = lineDiff;
  const hasChanges = added.length || removed.length || changed.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-slate-800">
          {t("offers.diff.lines_title")}
        </h4>
        <LineChip
          tone="bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
          icon={PlusCircleIcon}
          count={added.length}
          label={t("offers.diff.added")}
        />
        <LineChip
          tone="bg-red-50 text-red-700 ring-1 ring-red-200"
          icon={MinusCircleIcon}
          count={removed.length}
          label={t("offers.diff.removed")}
        />
        <LineChip
          tone="bg-amber-50 text-amber-800 ring-1 ring-amber-200"
          icon={PencilSquareIcon}
          count={changed.length}
          label={t("offers.diff.changed")}
        />
      </div>

      {!hasChanges ? (
        <p className="rounded-md bg-slate-50 px-4 py-6 text-center text-sm text-slate-500 ring-1 ring-slate-200">
          {t("offers.diff.no_line_changes")}
        </p>
      ) : (
        <div className="space-y-4">
          {added.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                {t("offers.diff.added")}
              </p>
              <ul className="space-y-2">
                {added.map((line) => (
                  <li
                    key={`add-${line.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border-l-4 border-emerald-400 bg-emerald-50/60 px-4 py-2 text-sm"
                  >
                    <LineTitle line={line} t={t} />
                    <span className="text-slate-600 tabular-nums">
                      {formatMoney(line.line_total)}
                      {currency ? ` ${currency}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {removed.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-red-700">
                {t("offers.diff.removed")}
              </p>
              <ul className="space-y-2">
                {removed.map((line) => (
                  <li
                    key={`rem-${line.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border-l-4 border-red-400 bg-red-50/60 px-4 py-2 text-sm"
                  >
                    <LineTitle line={line} t={t} />
                    <span className="text-slate-600 line-through tabular-nums">
                      {formatMoney(line.line_total)}
                      {currency ? ` ${currency}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {changed.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-800">
                {t("offers.diff.changed")}
              </p>
              <ul className="space-y-2">
                {changed.map(({ key, to, fields }) => (
                  <li
                    key={`chg-${key}`}
                    className="rounded-md border-l-4 border-amber-400 bg-amber-50/50 px-4 py-3 text-sm"
                  >
                    <LineTitle line={to} t={t} />
                    <div className="mt-2 space-y-1">
                      {fields.map((f) => (
                        <ChangedField
                          key={`${key}-${f.field}`}
                          field={f}
                          formatMoney={formatMoney}
                          currency={currency}
                          t={t}
                        />
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {unchanged > 0 && (
        <p className="text-xs text-slate-400">
          {t("offers.diff.unchanged_count", { count: unchanged })}
        </p>
      )}
    </div>
  );
}

function ChangedField({ field, formatMoney, currency, t }) {
  const fieldLabel = t(`offers.diff.field.${field.field}`);
  const renderValue = (value) => {
    if (field.kind === "money") {
      return `${formatMoney(value)}${currency ? ` ${currency}` : ""}`;
    }
    if (field.kind === "number") {
      const n = parseMoneyAmount(value);
      return n == null ? "–" : String(n);
    }
    const text = (value || "").trim();
    return text || "–";
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
      <span className="font-medium text-slate-500">{fieldLabel}:</span>
      <span className="text-slate-500 line-through">{renderValue(field.from)}</span>
      <ArrowRightGlyph />
      <span className="font-semibold text-slate-800">{renderValue(field.to)}</span>
    </div>
  );
}

function ArrowRightGlyph() {
  return (
    <svg
      className="h-3.5 w-3.5 text-slate-400"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
